import classNames from 'classnames';
import Button from 'components/base/Button';
import { Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { formatSecondsToMinutesAndSeconds } from 'helpers/utils';
import { useState, useEffect, ChangeEvent } from 'react';
import AudioPlayer from './AudioPlayer';
import { useTranslation } from 'react-i18next';
import AboutResourceCard from './AboutResourceCard';
import {
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAuth } from 'providers/AuthProvider'
import { ResourceTranscriptionInfo, UtteranceItem } from './DataDefinitions';

interface UtterancesProps {
    onSave?: () => void;
    onStartAnalysis: (resource_id: string, transcription_id: string, type: string) => void;

    resource_id: string;
    data: ResourceTranscriptionInfo;
    //transcription_id: string;
    //resource_meta: string;
    audioUrl: string;
    utterances: UtteranceItem[];
    speakers: { [key: string]: string };
    groupMeeting: boolean;
}


const Utterances = ({
    resource_id,
    data,
    //transcription_id,
    audioUrl,
    //resource_meta,
    utterances, speakers, groupMeeting,
    onStartAnalysis }: UtterancesProps) => {

    const auth = useAuth();
    const { t } = useTranslation();
    // Initialize state with a copy of the original list
    const [editedList, setEditedList] = useState<UtteranceItem[]>([...utterances]);

    const transcription_id = data.transcription.transcription_id || "";
    const resource_meta = data.resource.resource_meta;

    const resizeTextAreas = () => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        });
    };

    // Handler for updating the edited list
    const handleChange = (index: number, newValue: string) => {
        const updatedList = [...editedList];
        updatedList[index].text = newValue;
        setEditedList(updatedList);
    };

    // Dynamically adjust the textarea height based on content
    const handleTextareaResize = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        event.target.style.height = 'auto';
        event.target.style.height = `${event.target.scrollHeight}px`;
    };

    // Handler for creating a new item in the list on Enter key press
    const handleKeyPress = (event: any, index: number) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent default behavior (textarea newline)
            const { selectionStart, value } = event.target;
            const textBeforeEnter = value.substring(0, selectionStart);
            const textAfterEnter = value.substring(selectionStart);

            const updatedList = [...editedList];
            updatedList[index].text = textBeforeEnter;

            const newItem: UtteranceItem = { confidence: -1, end: -1, speaker: updatedList[index].speaker === 'A' ? 'B' : 'A', start: -1, text: textAfterEnter }
            updatedList.splice(index + 1, 0, newItem); // Insert text after Enter as a new item
            setEditedList(updatedList);
        }
    };

    // Dynamically adjust the textarea height based on content
    useEffect(() => {
        resizeTextAreas();
    }, [utterances, editedList]); // Run only once when component mounts

    const btn_get_feedback_status: boolean = JSON.parse(resource_meta)['default-feedback'] === 'ready';
    const [getFeedBackDisabled, setGetFeedBackDisabled] = useState<boolean>(btn_get_feedback_status);

    const [ICFFeedbackDisabled, setICFFeedbackDisabled] = useState<boolean>(JSON.parse(resource_meta)['icf-feedback'] === 'ready');


    // Define onClickHandler based on btn_status
    const onClickHandler = (type: string) => {
        if (type === 'default')
            setGetFeedBackDisabled(true);
        else
            setICFFeedbackDisabled(true);

        onStartAnalysis(resource_id, transcription_id, type);
    }

    const popover = (
        <Popover id="popover-basic" style={{ maxWidth: '500px' }} className="w-100 bg-200">
            <Popover.Body>
                <AboutResourceCard data={data} className="mb-1" />
            </Popover.Body>
        </Popover>
    );


    return (
        <>
            <Card className='mb-3'>
                <div className="d-flex p-2">
                    <AudioPlayer audioUrl={audioUrl} />

                    {!groupMeeting &&
                        <div className="p-2">
                            <Button
                                variant="primary"
                                className="ms-auto"
                            >
                                Save
                            </Button>
                        </div>
                    }
                    <div className="p-2">
                        <Button
                            variant={"primary"}
                            className="ms-auto"
                            disabled={getFeedBackDisabled}
                            onClick={() => (onClickHandler("default"))} // Pass onClickHandler here
                        >
                            {t("Utterances.get_feedback")}
                        </Button>
                    </div>

                    {!groupMeeting &&
                        auth.user?.is_coach &&
                        <div className="p-2">
                            <Button
                                variant="primary"
                                className="ms-auto"
                                disabled={ICFFeedbackDisabled}
                                onClick={() => (onClickHandler("ICF"))} // Pass onClickHandler here
                            >
                                {t("Utterances.get_icf_feedback")}
                            </Button>
                        </div>
                    }



                    <div className="p-2">
                        <OverlayTrigger trigger="click" placement="auto" overlay={popover}>
                            <span>
                                <Button
                                    variant="primary"

                                    startIcon={
                                        <FontAwesomeIcon icon={faCircleInfo} className="fs-9" />
                                    }
                                >
                                    Info
                                </Button>
                            </span>
                        </OverlayTrigger>
                    </div>
                </div>
            </Card>

            <Card>
                <div className="d-flex flex-column transcription-container scrollbar " style={{ maxHeight: '55vh', }}>
                    <Form>
                        {editedList.length > 0 && editedList.map((utterance: UtteranceItem, index: number) => (
                            <div key={resource_id + "-" + transcription_id + '-' + index}
                                className={classNames(
                                    {
                                        'border-bottom-0': index === editedList.length - 1
                                    },
                                    'd-flex flex-column align-items-start hover-actions-trigger border-bottom gap-2 todolist-item'
                                )}
                            >
                                <div className="d-flex mx-4 mb-1 align-content-center">

                                    <Badge
                                        variant="phoenix"
                                        bg={index % 2 === 0 ? 'primary' : 'secondary'}
                                        className="fs-10 ms-auto"
                                    >
                                        {speakers[utterance.speaker] || "Speaker: " + utterance.speaker}
                                    </Badge>

                                    <p className='text-700 fs-10 ms-3 mb-0'>
                                        {formatSecondsToMinutesAndSeconds(Math.round(utterance.start / 1000))}
                                    </p>

                                </div>

                                <Form.Control //key={'form-' + resource_id + '-' + transcription_id + '-' + index}
                                    as='textarea' readOnly={getFeedBackDisabled}
                                    //rows={2}
                                    value={utterance.text}
                                    onChange={(e) => {
                                        handleChange(index, e.target.value);
                                        handleTextareaResize(e);
                                    }}
                                    onKeyPress={(e) => handleKeyPress(e, index)}
                                    onInput={handleTextareaResize}
                                    className='border border-white pb-0 fw-semi-bold text-700 m-2'
                                    style={{
                                        resize: 'none', overflow: 'hidden'
                                    }} // Disable manual resize
                                />
                            </div>
                        ))}
                    </Form>
                </div>
            </Card>
        </>
    );
};
export default Utterances;
